import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ProgressBarModule } from 'primeng/progressbar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MilestonesComponent } from '../../../contract-management/components/milestones/milestones.component';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { IconComponent, IconSourceEnum } from '../../../@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent, MenuItemType } from '../../../@core/components/ui/menu/menu.component';
import { SelectComponent, SelectConfig, SelectOption } from '../../../@core/components/ui/select';
import { FILE_TYPE, STANDARD_DOCUMENT_TYPES } from '../../../@core/models/enums';
import { IAttachment, IMilestone, IShareUser, IUser, MILESTONE_TYPE, SHARE_ACCESS } from '../../../@core/models/model';
import { MileStoneService } from '../../../contract-management/services/milestone.service';
import { USER_TYPE } from '../../../@core/models/enums';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';
import { CaseType } from '../../../@shared/services/case-format.service';
import { GENERAL_AGENT_SVG } from '../../../common/svgs';
import { DOCUMENT_MODEL, KNOWLEDGE_SCOPE, CONTRACT_TYPE } from '../../../contract-management/models/contract-management.model';
import { IDocumentType } from '../../../contract-management/models/settings.model';
import { ContractService, DOCUMENT_CREATION_SOURCE } from '../../../contract-management/services/contracts.service';
import { DocumentTypeService } from '../../../contract-management/services/document-type.service';
import { ProjectService } from '../../../contract-management/services/project.service';
import { IntegrationsService, MailMessageInfo } from '../../../contract-management/services/integrations.service';
import { KnowledgeBaseService } from '../../../contract-management/services/knowledgebase.service';
import { RepositoryService } from '../../../contract-management/services/repository.service';
import { TemplateLibraryService } from '../../../contract-management/services/template-library.service';
import { AssetUrlPipe } from '../../pipes/asset-url.pipe';
import { TextCasePipe } from '../../pipes/textCase.pipe';
import { AttachmentGroupComponent } from '../attachment-group/attachment-group.component';
import { AttachmentFilterChip } from '../attachment-group/attachment-group-filter-types';
import { FileDropTypes } from '../file-drop/file-drop.types';
import {
  DropdownOption,
  FetchFunction,
  FileItem,
  FileSelectionChangeEvent,
  FileSelectionConfig,
  FileSelectionMode,
  FileSelectionSubmitEvent,
  TabType,
} from '../file-selection/file-selection.model';
import { FileSelectionComponent } from '../file-selection/file-selection.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { ShareToUsersComponent } from '../share-to-users/share-to-users.component';
import { StepperComponent } from '../stepper';
import { StepperStepDirective } from '../stepper-layout/stepper-step.directive';
import { StepperConfig, StepperStepChangeEvent, StepperStepConfig } from '../stepper-layout/stepper.types';
import { CONNECTOR_PROVIDER_INFO, CONNECTOR_PROVIDERS, TOOLKIT_PROVIDERS } from '../../../features/general-agent/constants/tool.constants';
import { CHAT_STYLES, ChatAttachment, QueryOptions } from '../../../features/general-agent/general-agent-types';
import { GeneralAgentService } from '../../../features/general-agent/services/general-agent.service';
import { DocumentPreviewComponent } from '../document-preview/document-preview.component';
import { getFileType } from '../../utils/utils';
import { DatepickerComponent } from '../../../@core/components/ui/datepicker/datepicker.component';
import { DatePickerConfig } from '../../../@core/components/ui/datepicker';
import { IProject } from '../../../@core/models/model';

const ALL_KNOWLEDGE_FOLDER_PREFIX = 'all-knowledge-';
const INTEGRATIONS_TAB_ID = 'INTEGRATIONS';
const INTEGRATIONS_TOOLKIT_PROVIDERS = [
  TOOLKIT_PROVIDERS.GOOGLE_DRIVE,
  TOOLKIT_PROVIDERS.ONE_DRIVE,
  TOOLKIT_PROVIDERS.SHAREPOINT,
  TOOLKIT_PROVIDERS.DROPBOX,
  TOOLKIT_PROVIDERS.OUTLOOK,
] as const;

const REF_DOC_CATEGORY = {
  ALL: 'ALL',
  WORKSPACE_KNOWLEDGE: 'WORKSPACE_KNOWLEDGE',
  PROJECT_KNOWLEDGE: 'PROJECT_KNOWLEDGE',
  DOCUMENTS: 'DOCUMENTS',
  FILES_FROM_LOCAL: 'FILES_FROM_LOCAL',
  INTEGRATIONS: 'INTEGRATIONS',
} as const;

const GENERATE_DOC_ATTACHMENT_CHIPS: AttachmentFilterChip[] = [
  { label: 'All', docModel: REF_DOC_CATEGORY.ALL },
  { label: 'Workspace Knowledge', docModel: REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE },
  { label: 'Project Knowledge', docModel: REF_DOC_CATEGORY.PROJECT_KNOWLEDGE },
  { label: 'Documents', docModel: REF_DOC_CATEGORY.DOCUMENTS },
  { label: 'Files from local', docModel: REF_DOC_CATEGORY.FILES_FROM_LOCAL },
  { label: 'Integrations', docModel: REF_DOC_CATEGORY.INTEGRATIONS },
];

@Component({
  selector: 'app-ai-assistant',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AccordionModule,
    ButtonModule,
    SelectModule,
    ProgressBarModule,
    RadioButtonModule,
    PaginatorModule,
    TooltipModule,
    ButtonComponent,
    MenuComponent,
    SelectComponent,
    TextCasePipe,
    AssetUrlPipe,
    IconComponent,
    SearchBarComponent,
    StepperComponent,
    StepperStepDirective,
    ShareToUsersComponent,

    FileSelectionComponent,
    DocumentPreviewComponent,
    DialogComponent,
    MilestonesComponent,
    DatepickerComponent,
  ],
  templateUrl: './ai-assistant.component.html',
  styleUrl: './ai-assistant.component.scss'
})
export class AiAssistantComponent implements OnInit, OnChanges, OnDestroy {
  readonly FileSelectionMode = FileSelectionMode;

  @ViewChild(FileSelectionComponent) fileSelectionRef?: FileSelectionComponent;
  @ViewChild('projectSelect') projectSelectRef?: SelectComponent;

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onDialogClose = new EventEmitter<void>();
  @Output() onGenerate = new EventEmitter<{ docTypeId: string, documentId?: any }>();

  milestones: IMilestone[] = [];
  milestonesLoading: boolean = false;
  openAccordionPanels: string[] = ['customisation'];

  docId!: string;
  CASE_TYPE = CaseType;
  IconSourceEnum = IconSourceEnum;

  // Customisation step
  content: string = '';
  loading: boolean = false;
  selectedProjectId: string = '';
  projectOptions: SelectOption[] = [];
  projectsLoading: boolean = false;
  private readonly projectsPageSize = 20;
  private projectsSkip = 0;
  private projectsTotal = 0;
  dueDate: Date | null = null;
  dueDateConfig: DatePickerConfig = {
    showIcon: true,
    placeholder: 'Select Due Date',
    dateFormat: 'dd M yy',
    appendTo: 'body',
    minDate: new Date(),
  }
  wordCount: number = 0;
  wordLimit: number = 500;
  docTypeLoading: boolean = false;
  docTypes: IDocumentType[] = [];
  docTypeOptions: SelectOption[] = [];
  selectedDocTypeId: string = '';
  selectedDocType: IDocumentType | null = null;
  currentStepIndex: number = 0;

  get docTypeConfig(): SelectConfig {
    return {
      placeholder: 'Select a Document Type',
      searchable: true,
      clearable: true,
      disabled: this.loading,
      loading: this.docTypeLoading,
      appendTo: 'body',
      panelStyleClass: 'max-w-[40vw]'
    };
  }

  aiAssistant: { open: boolean; content: string; response: string; stage: string; loading: boolean; loadingPercentage: number } = {
    open: false, content: '', response: '', stage: 'Igniting creativity – Starting with AI', loading: false, loadingPercentage: 0
  };

  public promptQuestions = [
    'Create a contract for general contractor services',
    'Draft a construction project subcontractor agreement',
    'Write a contract for a construction site inspection service'
  ];

  SVG_ICONS = GENERAL_AGENT_SVG;

  toneMenuItems: CustomMenuItem[] = [
    { label: 'Neutral', value: 'Neutral', icon: this.SVG_ICONS.NORMAL_STYLE, iconSource: IconSourceEnum.SVG },
    { label: 'Concise', value: 'Concise', icon: this.SVG_ICONS.CONCISE_STYLE, iconSource: IconSourceEnum.SVG },
    { label: 'Detailed', value: 'Detailed', icon: this.SVG_ICONS.EXPLANATORY_STYLE, iconSource: IconSourceEnum.SVG },
    { label: 'Formal', value: 'Formal', icon: this.SVG_ICONS.FORMAL_STYLE, iconSource: IconSourceEnum.SVG },
    { label: 'Bulletise', value: 'Bulletise', icon: this.SVG_ICONS.USE_STYLE, iconSource: IconSourceEnum.SVG },
  ];
  selectedTone: CustomMenuItem = { label: 'Neutral', value: 'Neutral', icon: this.SVG_ICONS.NORMAL_STYLE, iconSource: IconSourceEnum.SVG };
  toneMenuVisible = false;

  get selectedToneMenuValues(): CustomMenuItem[] {
    const item = this.toneMenuItems.find(i => i.value === this.selectedTone?.value);
    return item ? [item] : [];
  }
  get selectedToneLabel(): string {
    return this.selectedTone.label ?? 'Select tone';
  }

  // Library / Template step
  librarySearchValue = '';
  libraryTemplates: { _id: string; templateId?: string; name: string }[] = [];
  libraryLoading = false;
  libraryFirst = 0;
  libraryRows = 10;
  libraryTotalRecords = 0;
  libraryRowsPerPageOptions = [10, 20, 30];
  selectedLibraryItemId: string | null = null;

  selectedTemplateId: string | null = null;
  showViewTemplateDialog = false;
  selectedTemplateAttachment: IAttachment = {} as IAttachment;
  selectedTemplate: any = null;

  // Reference Documents step
  generateDocAttachmentChips = GENERATE_DOC_ATTACHMENT_CHIPS;
  referenceDocuments: { _id: string; name: string; docModel?: string; metadata?: Record<string, any> }[] = [];
  uploadedFiles: File[] = [];
  uploadDocumentsDialogVisible = false;
  parentReferenceDocument: FileItem | null = null;
  knowledgeBases: any[] = [];
  projects: any[] = [];
  fileSelectionDropdownOptions: DropdownOption[] = [];
  fileSelectionConfig: FileSelectionConfig = {
    maxTotalUploadedAttachments: 50,
    maxTotalUploadedFileSize: 500 * 1000 * 1000,
    localUploadMaxFileSize: 50 * 1000 * 1000,
    title: 'Upload files',
    showTotalAttachmentsCount: true,
    supportSubFolders: true,
    allowedFileTypes: [
      FileDropTypes.PDF, FileDropTypes.DOCX, FileDropTypes.DOC, FileDropTypes.XLSX,
      FileDropTypes.JPG, FileDropTypes.PNG, FileDropTypes.IMAGE, FileDropTypes.JPEG,
      FileDropTypes.MD, FileDropTypes.TEXT,
    ],
    tabs: [
      { id: QueryOptions.KNOWLEDGE_BASE, label: 'Workspace Knowledge', tooltip: 'Select from knowledge base files', type: TabType.SELECT, folderIcon: 'book-open-text', folderIconSource: 'lucide' },
      { id: QueryOptions.PROJECT_KNOWLEDGE, label: 'Project Knowledge', tooltip: 'Select from project knowledge', type: TabType.SELECT, folderIcon: 'book-open-text', folderIconSource: 'lucide' },
      { id: QueryOptions.PROJECT_FILES, label: 'Documents', tooltip: 'Select from home documents', type: TabType.SELECT, folderIcon: 'book-open-text', folderIconSource: 'lucide' },
      { id: QueryOptions.ADD_PHOTOS_AND_FILES, label: 'Files from local', tooltip: 'Select from local files', type: TabType.UPLOAD },
      { id: 'INTEGRATIONS', label: 'Integrations', tooltip: 'Select from integrations', type: TabType.SELECT, folderIcon: 'folder', folderIconSource: 'lucide' },
    ],
  };

  integrationsConnectorsMenuItems: CustomMenuItem[] = [];
  integrationPaginationState: { [key: string]: { nextPageToken?: string; nextCursor?: string } } = {};
  private outlookMailsCache = new Map<string, MailMessageInfo>();

  private readonly manageConnectorsMenuItem: CustomMenuItem = {
    label: 'Manage Connectors',
    value: 'manage-connectors',
    icon: 'settings-2',
    type: MenuItemType.SINGLE_SELECT,
    iconSource: IconSourceEnum.LUCIDE,
    subOptions: [],
    buttonCallback: () => { this.router.navigate(['/settings/integrations']); },
  };

  get referenceDocumentsAsChatAttachments(): ChatAttachment[] {
    return this.referenceDocuments.map((d) => ({
      _id: d._id,
      name: d.name,
      docModel: (d.docModel as DOCUMENT_MODEL) ?? DOCUMENT_MODEL.CONTRACT,
      type: getFileType(d.name) || FILE_TYPE.PDF,
      category: this.getReferenceDocCategory(d),
    }));
  }

  refDocSourceFn = (a: ChatAttachment): string => {
    switch (a.category) {
      case REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE: return 'Workspace Knowledge';
      case REF_DOC_CATEGORY.PROJECT_KNOWLEDGE: return 'Project Knowledge';
      case REF_DOC_CATEGORY.DOCUMENTS: return 'Documents';
      case REF_DOC_CATEGORY.FILES_FROM_LOCAL: return 'Files from local';
      case REF_DOC_CATEGORY.INTEGRATIONS: return 'Integrations';
      default: return 'Reference document';
    }
  };

  // Team step
  shareDetails: { users: IShareUser[]; anyone: SHARE_ACCESS; workspaceShare?: boolean; projectShare?: boolean } = {
    users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false,
  };

  // Stepper
  generationInProgress = false;
  stepperConfig!: StepperConfig;

  customisationStepConfig: StepperStepConfig = {
    id: 'customisation',
    label: 'Customization',
    title: 'Customization',
    subtitle: 'Select a template, customize by your preference',
    isValid: () => !!this.content.trim() && !!this.selectedDocType,
    invalidMessage: 'Please select a document type and enter a prompt',
  };

  templateStepConfig: StepperStepConfig = {
    id: 'template',
    label: 'Template',
    title: 'Template',
    subtitle: 'Select a template, customize by your preference',
  };

  referenceDocsStepConfig: StepperStepConfig = {
    id: 'reference-documents',
    label: 'Reference Documents',
    title: 'Reference Documents',
    subtitle: 'Select a template, customize by your preference',
    contentClass: 'px-2',
    isValid: () => this.hasReferenceDocuments(),
    invalidMessage: 'Please add at least one reference document',
  };

  teamStepConfig: StepperStepConfig = {
    id: 'team',
    label: 'Team',
    title: 'Team',
    subtitle: 'Select a template, customize by your preference',
  };

  constructor(
    private toastService: ToastService,
    private contractService: ContractService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private documentTypeService: DocumentTypeService,
    private templateLibraryService: TemplateLibraryService,
    private knowledgeBaseService: KnowledgeBaseService,
    private repositoryService: RepositoryService,
    private integrationsService: IntegrationsService,
    private projectService: ProjectService,
    private authenticationService: AuthenticationService,
    private milestoneService: MileStoneService,
    private userCreditsState: UserCreditsStateService,
  ) {
    this.docId = this.activatedRoute.snapshot.queryParams['parentDocId'] || this.activatedRoute.snapshot.queryParams['documentId'];
  }

  ngOnInit(): void {
    this.stepperConfig = {
      subtitle: 'A completely new way to handle your construction documents',
      steps: [this.customisationStepConfig, this.templateStepConfig, this.referenceDocsStepConfig, this.teamStepConfig],
    };
    this.getDocTypes();
    this.loadProjects();
    this.loadParentReferenceDocument();
    this.ensureDocumentOwnerInShareDetails();
    (window as any)['onConnectorConnected'] = () => this.getIntegrationsConnectors();
  }

  ngOnDestroy(): void {
    delete (window as any)['onConnectorConnected'];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.checkJobInProgress();
    }
  }

  isNextDisabled(): boolean {
    if (this.currentStepIndex === 0) {
      return !this.content.trim() || !this.selectedDocType;
    }
    return false;
  }

  onStepChange(event: StepperStepChangeEvent): void {
    this.currentStepIndex = event.currentIndex;
    if (event.currentIndex === 2) {
      this.loadKnowledgeBasesForFileSelection();
      this.loadProjectsForDropdown();
      this.getIntegrationsConnectors();
    }
  }

  onNextStep(): void {
    if (this.currentStepIndex === 0) {
      this.libraryFirst = 0;
      this.librarySearchValue = '';
      this.loadLibraryTemplates();
    }
    if (this.currentStepIndex === 2) {
      this.fileSelectionRef?.handleSubmit();
    }
  }

  // Auth / lifecycle
  private ensureDocumentOwnerInShareDetails(): void {
    const hasOwner = this.shareDetails.users.some(u => u.role === SHARE_ACCESS.OWNER);
    if (hasOwner) return;
    const currentUser = this.authenticationService.getUser();
    if (currentUser) {
      this.shareDetails.users = [...this.shareDetails.users, {
        name: currentUser.fullName ?? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' '),
        user: currentUser as IUser,
        email: currentUser.email as string,
        type: USER_TYPE.INTERNAL,
        role: SHARE_ACCESS.OWNER,
      } as IShareUser];
    }
  }

  cancel(): void {
    this.resetAll();
    this.visible = false;
    this.visibleChange.emit(false);
    this.onDialogClose.emit();
  }

  private resetAll(): void {
    this.content = '';
    this.loading = false;
    this.aiAssistant.loading = false;
    this.aiAssistant.stage = 'Igniting creativity - Starting with AI!';
    this.aiAssistant.loadingPercentage = 0;
    this.selectedDocTypeId = '';
    this.selectedDocType = null;
    this.currentStepIndex = 0;
    this.selectedLibraryItemId = null;
    this.librarySearchValue = '';
    this.libraryFirst = 0;
    this.referenceDocuments = [];
    this.syncFileSelectionInitialFiles();
    this.uploadedFiles = [];
    this.uploadDocumentsDialogVisible = false;
    this.shareDetails = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
    this.ensureDocumentOwnerInShareDetails();
  }

  // Projects
  loadProjects(): void {
    this.projectsSkip = 0;
    this.projectsTotal = 0;
    this.projectOptions = [];
    this.projectSelectRef?.setHasMore(true);
    this.fetchProjectsPage();
  }

  private fetchProjectsPage(): void {
    if (this.projectsLoading) return;
    this.projectsLoading = true;
    this.projectService.getProjects(this.projectsSkip, this.projectsPageSize).subscribe(res => {
      this.projectsLoading = false;
      if (res?.success && res.data?.projects) {
        this.projectsTotal = res.data.totalRecords ?? 0;
        this.projectsSkip += res.data.projects.length;
        const newOptions = res.data.projects.map((p: IProject) => ({ label: p.name, value: p._id }));
        this.projectOptions = [...this.projectOptions, ...newOptions];
        this.projectSelectRef?.setHasMore(this.projectsSkip < this.projectsTotal);
      }
    });
  }

  onProjectSearch(event: { query: string }): void {
    const query = event.query.trim();
    if (!query) {
      this.loadProjects();
      return;
    }
    this.projectsLoading = true;
    this.projectService.getProjects(0, 50, { searchValue: query }).subscribe(res => {
      this.projectsLoading = false;
      if (res?.success && res.data?.projects) {
        this.projectsSkip = 0;
        this.projectsTotal = 0;
        this.projectOptions = res.data.projects.map((p: IProject) => ({ label: p.name, value: p._id }));
      }
    });
  }

  onProjectLoadMore(): void {
    if (this.projectsSkip < this.projectsTotal) {
      this.fetchProjectsPage();
    } else {
      this.projectSelectRef?.setHasMore(false);
      this.projectSelectRef?.setLoading(false);
    }
  }

  private loadParentReferenceDocument(): void {
    if (!this.docId) return;
    this.contractService.fetchContract(this.docId).subscribe({
      next: (res: any) => {
        const doc = res?.data ?? res;
        if (!doc?._id) return;
        const dropdownId = doc.project?._id || doc.project || 'all';
        const projectName = doc.project?.name || 'All documents';
        this.parentReferenceDocument = {
          id: doc._id,
          name: doc.name || 'Document',
          metadata: {
            tabId: QueryOptions.PROJECT_FILES,
            dropdownId,
            projectId: dropdownId,
            projectName,
            docModel: DOCUMENT_MODEL.CONTRACT,
          },
        };
        this.syncFileSelectionInitialFiles();
      },
      error: () => {
        this.parentReferenceDocument = null;
      }
    });
  }

  // Doc types
  getDocTypes(): void {
    this.docTypeLoading = true;
    this.documentTypeService.getDocTypes().subscribe(res => {
      this.docTypeLoading = false;
      if (res?.success) {
        this.docTypes = (res.data || []).filter((dt: IDocumentType) => dt.name !== STANDARD_DOCUMENT_TYPES.CONTRACT);
        this.docTypeOptions = this.docTypes.map(dt => ({ label: dt.name, value: dt._id, data: dt }));
      }
    });
  }

  handleDocTypeChange(event: any): void {
    this.selectedDocTypeId = event.value;
    this.selectedDocType = this.docTypes.find(dt => dt._id === event.value) || null;
    this.getDocTypeMilestones();
  }

  onMilestonesChange(milestones: Partial<IMilestone>[]): void {
    this.milestones = milestones as IMilestone[];
  }

  getDocTypeMilestones(): void {
    if (!this.selectedDocType?._id) {
      this.milestones = [];
      this.openAccordionPanels = this.openAccordionPanels.filter(p => p !== 'milestones');
      return;
    }
    this.milestonesLoading = true;
    this.milestoneService.getMilestones(MILESTONE_TYPE.DOCUMENT, undefined, undefined, this.selectedDocType._id).subscribe(res => {
      this.milestonesLoading = false;
      if (res.success) {
        this.milestones = (res.data || []).map((m: IMilestone) => ({
          ...m,
          date: m.date ? new Date(m.date) : new Date(new Date().setHours(23, 59, 59, 999)),
        }));
      }
    });
  }

  // Tone
  onToneSelected(item: CustomMenuItem): void {
    if (item.value != null) this.selectedTone = item;
    this.toneMenuVisible = false;
  }

  // Prompt
  sendPrompt(prompt: string): void {
    this.content = prompt;
    this.checkWordLimit();
  }

  checkWordLimit(): void {
    this.wordCount = this.getWordCount(this.content);
    if (this.wordCount > this.wordLimit) {
      this.content = this.content.split(/\s+/).slice(0, this.wordLimit).join(' ');
    }
  }

  getWordCount(content: string): number {
    if (!content) return 0;
    return content.trim().split(/\s+/).length;
  }

  // Library: templates
  loadLibraryTemplates(): void {
    this.libraryLoading = true;
    this.templateLibraryService.getTemplates({
      search: this.librarySearchValue || undefined,
      skip: this.libraryFirst,
      limit: this.libraryRows,
      documentType: this.selectedDocTypeId || undefined,
    }).subscribe({
      next: (res: any) => {
        const payload = res?.data ?? res;
        this.libraryTemplates = Array.isArray(payload?.data) ? payload.data : [];
        this.libraryTotalRecords = payload?.total ?? 0;
        this.libraryLoading = false;
      },
      error: () => { this.libraryLoading = false; }
    });
  }

  onLibrarySearchChange(): void {
    this.libraryFirst = 0;
    this.loadLibraryTemplates();
  }

  onLibraryPageChange(event: PaginatorState): void {
    this.libraryFirst = event.first ?? 0;
    this.libraryRows = event.rows ?? this.libraryRows;
    this.loadLibraryTemplates();
  }

  selectLibraryItem(id: string): void {
    this.selectedLibraryItemId = this.selectedLibraryItemId === id ? null : id;
  }

  onTemplateRadioClick(event: Event, itemId: string): void {
    if (this.selectedLibraryItemId === itemId) {
      this.selectedLibraryItemId = null;
      event.preventDefault();
    }
    event.stopPropagation();
  }

  onNewTemplate(): void {
    window.open(
      this.router.serializeUrl(this.router.createUrlTree(['/settings/library/template-library'])),
      '_blank'
    );
  }

  onViewLibraryItem(item: { _id: string; templateId?: string; name: string }): void {
    this.templateLibraryService.getTemplate(item._id).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.selectedTemplate = res.data;
          this.selectedTemplateAttachment = res.data.attachment as IAttachment;
          this.selectedTemplateId = item._id;
          this.showViewTemplateDialog = true;
        } else {
          this.toastService.error('Failed to load template');
        }
      },
      error: () => { this.toastService.error('Failed to load template'); }
    });
  }

  // Reference documents
  private hasReferenceDocuments(): boolean {
    return this.referenceDocuments.length > 0 || !!this.parentReferenceDocument || this.hasSelectedReferenceDocuments();
  }

  private hasSelectedReferenceDocuments(): boolean {
    return (this.fileSelectionRef?.allSelectedFiles()?.length ?? 0) > 0;
  }

  openUploadDocumentsDialog(): void {
    this.loadKnowledgeBasesForFileSelection();
    this.loadProjectsForDropdown();
    this.getIntegrationsConnectors();
    this.syncFileSelectionInitialFiles();
    this.uploadDocumentsDialogVisible = true;
  }

  loadKnowledgeBasesForFileSelection(): void {
    this.knowledgeBaseService.getKnowledgeBaseStore({ skip: 0, limit: 1000, scope: KNOWLEDGE_SCOPE.WORKSPACE }).subscribe(res => {
      if (res?.success && res?.data?.stores) {
        this.knowledgeBases = res.data.stores;
        this.prepareFileSelectionDropdownOptions();
      }
    });
  }

  loadProjectsForDropdown(): void {
    this.projectService.getProjects(0, 1000).subscribe((res: any) => {
      if (res?.success) this.projects = res.data?.projects ?? [];
    });
  }

  private prepareFileSelectionDropdownOptions(): void {
    const options: DropdownOption[] = [{ id: 'all', label: 'All files', groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' }];
    this.knowledgeBases.forEach(kb => {
      options.push({ id: kb._id, label: kb.name, value: kb, groupKey: 'metadata.kbId', groupLabel: 'metadata.kbName' });
    });
    this.fileSelectionDropdownOptions = options;
  }

  private prepareDocumentsDropdownOptions(): void {
    const options: DropdownOption[] = [{ id: 'all', label: 'All documents', groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' }];
    this.projects.forEach(p => options.push({ id: p._id, label: p.name, value: p, groupKey: 'metadata.projectId', groupLabel: 'metadata.projectName' }));
    this.fileSelectionDropdownOptions = options;
  }

  private prepareProjectKnowledgeDropdownOptions(): void {
    const options: DropdownOption[] = [];
    this.projects.forEach(p => options.push({ id: p._id, label: p.name, value: p, groupKey: 'metadata.kbId', groupLabel: 'metadata.breadcrumbLabel' }));
    this.fileSelectionDropdownOptions = options;
  }

  private prepareIntegrationsDropdownOptions(): void {
    this.fileSelectionDropdownOptions = INTEGRATIONS_TOOLKIT_PROVIDERS.map(provider => {
      const info = CONNECTOR_PROVIDER_INFO[provider];
      return { id: provider, label: info.name, value: provider, icon: info.icon, iconSource: IconSourceEnum.SVG, iconSize: 'lg', iconClass: '[&>svg]:w-4 [&>svg]:h-4' };
    });
  }

  onRemoveReferenceAttachment(attachment: ChatAttachment): void {
    this.referenceDocuments = this.referenceDocuments.filter(d => d._id !== attachment._id);
    this.syncFileSelectionInitialFiles();
  }

  private syncFileSelectionInitialFiles(): void {
    const initialSelectedFilesById = new Map<string, FileItem>();

    this.referenceDocuments.forEach(d => {
      initialSelectedFilesById.set(d._id, {
        id: d._id,
        name: d.name,
        metadata: {
          ...(d.metadata ?? {}),
          docModel: d.docModel ?? d.metadata?.['docModel'],
        },
      });
    });

    if (this.parentReferenceDocument) {
      initialSelectedFilesById.set(this.parentReferenceDocument.id, this.parentReferenceDocument);
    }

    this.fileSelectionConfig = {
      ...this.fileSelectionConfig,
      initialSelectedFiles: Array.from(initialSelectedFilesById.values()),
    };
  }

  private getReferenceDocCategory(d: { docModel?: string; metadata?: Record<string, any> }): string {
    if (d.metadata?.['connector'] && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(d.metadata['connector'] as any)) return REF_DOC_CATEGORY.INTEGRATIONS;
    if (d.metadata?.['isProjectKnowledge']) return REF_DOC_CATEGORY.PROJECT_KNOWLEDGE;
    if (d.metadata?.['kbId']) return REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE;
    if (d.docModel === DOCUMENT_MODEL.CONTRACT || d.metadata?.['projectId']) return REF_DOC_CATEGORY.DOCUMENTS;
    if (d.docModel === DOCUMENT_MODEL.ATTACHMENT) return REF_DOC_CATEGORY.FILES_FROM_LOCAL;
    if (d.docModel === DOCUMENT_MODEL.REPOSITORY) return REF_DOC_CATEGORY.WORKSPACE_KNOWLEDGE;
    return REF_DOC_CATEGORY.FILES_FROM_LOCAL;
  }

  // Integrations
  getIntegrationsConnectors(): void {
    this.integrationsService.getConnectors().subscribe((res: any) => {
      if (res?.success && res.data) {
        const allowedSet = new Set<string>(INTEGRATIONS_TOOLKIT_PROVIDERS);
        const connectorMenuItems: CustomMenuItem[] = [];
        for (const category of res.data) {
          if (category?.values && Array.isArray(category.values)) {
            for (const connector of category.values) {
              if (!allowedSet.has(connector.name) || !(CONNECTOR_PROVIDER_INFO as any)[connector.name]) continue;
              const mapping = (CONNECTOR_PROVIDER_INFO as any)[connector.name];
              let menuItem: CustomMenuItem = { label: mapping.name, value: connector.name, icon: mapping.icon, iconSource: IconSourceEnum.SVG, subOptions: [], class: '[&>svg]:w-4 [&>svg]:h-4' };
              if (connector.toolkit === true) menuItem = { ...menuItem, type: MenuItemType.TOGGLE, toggleState: true };
              else if (connector.toolkit === false) menuItem = { ...menuItem, type: MenuItemType.TOGGLE, toggleState: false };
              else if (connector.toolkit === null) menuItem = { ...menuItem, type: MenuItemType.BUTTON, buttonLabel: 'Connect', buttonClass: 'theme-text border-none' };
              connectorMenuItems.push(menuItem);
            }
          }
        }
        this.integrationsConnectorsMenuItems = [...connectorMenuItems, this.manageConnectorsMenuItem];
      }
    });
  }

  getIntegrationConnectorConnected(connectorId: string | null): boolean {
    if (!connectorId) return false;
    const item = this.integrationsConnectorsMenuItems.find(m => m.value === connectorId);
    return item?.type === MenuItemType.TOGGLE && item?.toggleState === true;
  }

  getIntegrationConnectorToolkitStatus(connectorId: string | null): 'connected' | 'null' | 'disabled' | null {
    if (!connectorId) return null;
    const item = this.integrationsConnectorsMenuItems.find(m => m.value === connectorId);
    if (!item) return null;
    if (item.type === MenuItemType.BUTTON) return 'null';
    if (item.type === MenuItemType.TOGGLE) return item.toggleState === true ? 'connected' : 'disabled';
    return null;
  }

  onIntegrationsConnectorSelect(option: CustomMenuItem, menu?: MenuComponent): void {
    if (option.value === 'manage-connectors') { this.router.navigate(['/settings/integrations']); return; }
    const toolKitValue = option.value as TOOLKIT_PROVIDERS;
    if (option.type === MenuItemType.BUTTON) {
      menu?.hide();
      option.buttonLoading = true; option.disabled = true; option.buttonLabel = '';
      this.integrationsService.addConnector({ toolKit: toolKitValue, callbackUrl: 'explore/callback' }).subscribe((res: any) => {
        if (res?.data?.status) {
          this.toastService.success('Connection initiated successfully');
          if (res.data.redirectUrl) {
            (window as any)['onConnectorConnected'] = () => this.getIntegrationsConnectors();
            window.open(res.data.redirectUrl, '_blank');
          }
        } else { this.toastService.error('Connection failed'); option.buttonLoading = false; option.disabled = false; option.buttonLabel = 'Connect'; }
        this.getIntegrationsConnectors();
      });
      return;
    }
    if (option.toggleState) {
      this.integrationsService.enableConnector(toolKitValue).subscribe(() => this.getIntegrationsConnectors());
    } else {
      this.integrationsService.disableConnector(toolKitValue).subscribe(() => this.getIntegrationsConnectors());
    }
  }

  fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent) => {
    if (event.tabId === QueryOptions.KNOWLEDGE_BASE) {
      if (event.dropdownId === 'all') return this.fetchAllKnowledgeBaseFiles(event);
      if (event.dropdownId) return this.fetchKnowledgeBaseFiles(event.dropdownId, event);
      this.fileSelectionDropdownOptions = [];
      this.prepareFileSelectionDropdownOptions();
      return of({ items: [], totalRecords: 0 });
    }
    if (event.tabId === QueryOptions.PROJECT_FILES) {
      if (event.dropdownId === 'all') return this.fetchAllProjectDocuments(event);
      if (event.dropdownId) return this.fetchProjectDocuments(event.dropdownId, event);
      this.fileSelectionDropdownOptions = [];
      this.prepareDocumentsDropdownOptions();
      return of({ items: [], totalRecords: 0 });
    }
    if (event.tabId === QueryOptions.PROJECT_KNOWLEDGE) {
      if (event.dropdownId === 'all') return this.fetchAllProjectKnowledgeFiles(event);
      if (event.dropdownId) return this.fetchProjectKnowledgeFiles(event.dropdownId, event);
      this.fileSelectionDropdownOptions = [];
      this.prepareProjectKnowledgeDropdownOptions();
      return of({ items: [], totalRecords: 0 });
    }
    if (event.tabId === INTEGRATIONS_TAB_ID) {
      this.prepareIntegrationsDropdownOptions();
      if (event.dropdownId && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(event.dropdownId as any)) return this.fetchIntegrationsFiles(event);
      return of({ items: [], totalRecords: 0 });
    }
    return of({ items: [], totalRecords: 0 });
  };

  private fetchAllKnowledgeBaseFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.WORKSPACE }).pipe(
      map((res) => {
        const allFiles: FileItem[] = [];
        const files = (res?.data || []).filter((f: any) => f.type === 'FILE');
        const filtered = event.searchQuery ? files.filter((f: any) => (f.name || '').toLowerCase().includes((event.searchQuery || '').toLowerCase())) : files;
        filtered.forEach((file: any) => {
          const firstKb = file.knowledgeBases?.[0];
          const kbDetails = firstKb ? this.knowledgeBases.find(k => k._id === firstKb._id) : undefined;
          allFiles.push({ id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { kbId: kbDetails?._id ?? 'file-repository', kbName: kbDetails?.name ?? 'Files', originalFile: file } });
        });
        const start = event.page * event.pageSize;
        return { items: allFiles.slice(start, start + event.pageSize), totalRecords: allFiles.length };
      })
    );
  }

  private fetchKnowledgeBaseFiles(kbId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    const kb = this.knowledgeBases.find(k => k._id === kbId);
    if (!kb?.files) return of({ items: [], totalRecords: 0 });
    let items: FileItem[] = kb.files
      .filter((f: any) => !event.searchQuery || (f.file?.name || '').toLowerCase().includes((event.searchQuery || '').toLowerCase()))
      .map((f: any) => ({ id: f.file?._id, name: f.file?.name || 'Unknown', size: f.file?.size, metadata: { kbId: kb._id, kbName: kb.name, originalFile: f.file } }));
    const start = event.page * event.pageSize;
    return of({ items: items.slice(start, start + event.pageSize), totalRecords: items.length });
  }

  private fetchAllProjectDocuments(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareDocumentsDropdownOptions();
    return this.contractService.getContracts({ type: CONTRACT_TYPE.ALL, skip: event.page * event.pageSize, limit: event.pageSize, name: event.searchQuery || '' }).pipe(
      map((res) => {
        const documents: FileItem[] = [];
        if (res?.success && res.data?.contracts) {
          res.data.contracts.forEach((doc: any) => {
            const project = this.projects.find(p => p._id === (doc.projectId || doc.project?._id));
            documents.push({ id: doc._id, name: doc.name || 'Unknown', size: doc?.fileSize ?? 0, metadata: { projectId: project?._id || 'no-project', projectName: project?.name || 'No project', originalDoc: doc } });
          });
        }
        return { items: documents, totalRecords: res?.data?.total ?? 0 };
      })
    );
  }

  private fetchProjectDocuments(projectId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareDocumentsDropdownOptions();
    const project = this.projects.find(p => p._id === projectId);
    if (!project) return of({ items: [], totalRecords: 0 });
    return this.contractService.getContracts({ type: CONTRACT_TYPE.ALL, skip: event.page * event.pageSize, limit: event.pageSize, name: event.searchQuery || '', projectId }).pipe(
      map((res) => {
        if (res?.success && res.data?.contracts) {
          return { items: res.data.contracts.map((doc: any) => ({ id: doc._id, name: doc.name || 'Unknown', metadata: { projectId: project._id, projectName: project.name, originalDoc: doc } })), totalRecords: res.data.total ?? 0 };
        }
        return { items: [], totalRecords: 0 };
      })
    );
  }

  private fetchAllProjectKnowledgeFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareProjectKnowledgeDropdownOptions();
    if (event.parentFolderId) {
      return this.repositoryService.getFolderDetails(event.parentFolderId).pipe(
        map((res: any) => {
          const childs = res?.data?.childs ?? res?.childs ?? [];
          const filtered = childs.filter((f: any) => !event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
          const allItems: FileItem[] = filtered.map((file: any) => {
            if (file.type === 'FOLDER') return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
            const project = this.projects.find(p => p._id === (file.project?._id ?? file.project));
            return { id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project?._id ?? 'no-project', projectName: project?.name ?? 'No project', originalFile: file, isProjectKnowledge: true } };
          });
          const start = event.page * event.pageSize;
          return { items: allItems.slice(start, start + event.pageSize), totalRecords: allItems.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }
    return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT }).pipe(
      map((res) => {
        const raw = (res as any)?.data ?? [];
        const filtered = raw.filter((f: any) => !event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
        const allFiles: FileItem[] = filtered.map((file: any) => {
          if (file.type === 'FOLDER') return { id: file._id, name: file.name || 'Unknown', isFolder: true, metadata: {} };
          const project = this.projects.find(p => p._id === (file.project?._id ?? file.project));
          return { id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project?._id ?? 'no-project', projectName: project?.name ?? 'No project', originalFile: file, isProjectKnowledge: true } };
        });
        const start = event.page * event.pageSize;
        return { items: allFiles.slice(start, start + event.pageSize), totalRecords: allFiles.length };
      })
    );
  }

  private fetchProjectKnowledgeFiles(projectId: string, event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    this.prepareProjectKnowledgeDropdownOptions();
    const project = this.projects.find(p => p._id === projectId);
    if (!project) return of({ items: [], totalRecords: 0 });
    const allKnowledgeFolderId = ALL_KNOWLEDGE_FOLDER_PREFIX + projectId;
    if (event.parentFolderId === allKnowledgeFolderId) {
      return this.repositoryService.getFiles({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId }).pipe(
        map((res: any) => {
          const raw = res?.data ?? [];
          const items: FileItem[] = raw.filter((f: any) => f.type === 'FILE' && (!event.searchQuery || f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase())))
            .map((file: any) => ({ id: file._id, name: file.name || 'Unknown', size: file.size, metadata: { projectId: project._id, projectName: project.name, originalFile: file, isProjectKnowledge: true } }));
          const start = event.page * event.pageSize;
          return { items: items.slice(start, start + event.pageSize), totalRecords: items.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }
    if (event.parentFolderId) {
      return this.knowledgeBaseService.getKnowledgeBaseStoreById(event.parentFolderId).pipe(
        map((res: any) => {
          const store = res?.data ?? res;
          if (!store?.files) return { items: [], totalRecords: 0 };
          const breadcrumbLabel = `${project.name} > ${store.name || 'Unknown'}`;
          let list: FileItem[] = store.files.map((item: any) => ({ id: item.file?._id, name: item.file?.name || 'Unknown', size: item.file?.size, metadata: { projectId: project._id, projectName: project.name, kbId: store._id, kbName: store.name, breadcrumbLabel, originalFile: item.file, isProjectKnowledge: true } }));
          if (event.searchQuery) list = list.filter(f => f.name?.toLowerCase().includes(event.searchQuery!.toLowerCase()));
          const start = event.page * event.pageSize;
          return { items: list.slice(start, start + event.pageSize), totalRecords: list.length };
        }),
        catchError(() => of({ items: [], totalRecords: 0 }))
      );
    }
    return this.knowledgeBaseService.getKnowledgeBaseStore({ scope: KNOWLEDGE_SCOPE.PROJECT, project: projectId, skip: event.page === 0 ? 0 : event.page * event.pageSize - 1, limit: event.page === 0 ? event.pageSize - 1 : event.pageSize, search: event.searchQuery || '' }).pipe(
      map((res) => {
        const stores = res?.data?.stores ?? [];
        const storeItems: FileItem[] = stores.map((s: any) => ({ id: s._id, name: s.name || 'Unknown', isFolder: true, metadata: { kbId: s._id, kbName: s.name } }));
        const allKnowledgeFolder: FileItem = { id: allKnowledgeFolderId, name: 'All Knowledge', isFolder: true, metadata: {} };
        const items: FileItem[] = event.page === 0 ? [allKnowledgeFolder, ...storeItems] : storeItems;
        return { items, totalRecords: (res?.data?.total ?? 0) + 1 };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  private fetchIntegrationsFiles(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    const connector = event.dropdownId ?? '';
    const status = this.getIntegrationConnectorToolkitStatus(connector);
    if (status === 'null') { this.toastService.warn('Connect your account from the Connectors menu to view files.'); return of({ items: [], totalRecords: 0 }); }
    if (status === 'disabled') { this.toastService.warn('Enable this connector from the Connectors menu to view files.'); return of({ items: [], totalRecords: 0 }); }
    if (status !== 'connected') return of({ items: [], totalRecords: 0 });
    if (connector === TOOLKIT_PROVIDERS.OUTLOOK) return this.fetchOutlookMails(event);
    const pageSize = event.pageSize || 20;
    const key = `${event.tabId}:${connector}:${event.parentFolderId ?? 'root'}`;
    const saved = this.integrationPaginationState[key];
    const params: any = { connector, pageSize, page: event.page === 0 ? 1 : event.page + 1 };
    let fullSiteUrl: string | undefined;
    if (event.parentFolderId) {
      if (connector === TOOLKIT_PROVIDERS.GOOGLE_DRIVE) params.folderId = event.parentFolderId;
      else if (connector === TOOLKIT_PROVIDERS.ONE_DRIVE) params.folderPath = event.parentFolderId;
      else if (connector === TOOLKIT_PROVIDERS.DROPBOX) params.folderPath = event.parentFolderId;
      else if (connector === TOOLKIT_PROVIDERS.SHAREPOINT) {
        if (event.parentFolderId.includes('|||')) {
          const [a, b] = event.parentFolderId.split('|||', 2);
          if (a.startsWith('http')) { fullSiteUrl = a; params.siteUrl = a; params.serverRelativeUrl = b; }
          else { fullSiteUrl = b; params.siteUrl = b; }
        } else { params.serverRelativeUrl = event.parentFolderId; }
      }
    }
    if (event.page > 0 && saved) { if (saved.nextPageToken) params.pageToken = saved.nextPageToken; if (saved.nextCursor) params.cursor = saved.nextCursor; }
    return this.integrationsService.listFiles(params).pipe(
      map((res: any) => {
        const payload = res?.data?.data ?? res;
        const dataKind = res?.data?.dataKind;
        const normalized = connector === TOOLKIT_PROVIDERS.SHAREPOINT && dataKind === 'sites'
          ? this.normalizeSharepointSites(payload)
          : this.normalizeIntegrationListResponse(connector, payload, pageSize, fullSiteUrl);
        const nextPageToken = res?.data?.hasMore ? res?.data?.nextPageToken : normalized.nextPageToken;
        const nextCursor = res?.data?.nextCursor ?? normalized.nextCursor;
        if (nextPageToken || nextCursor) this.integrationPaginationState[key] = { nextPageToken, nextCursor };
        return { items: normalized.items, totalRecords: normalized.items.length + (res?.data?.hasMore ? pageSize : 0) };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  private fetchOutlookMails(event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> {
    const connector = TOOLKIT_PROVIDERS.OUTLOOK;
    if (event.parentFolderId) {
      const mail = this.outlookMailsCache.get(event.parentFolderId);
      if (mail) {
        const items: FileItem[] = (mail.attachments ?? []).map((a) => ({
          id: a.id,
          name: a.name ?? 'Attachment',
          size: a.size,
          isFolder: false,
          metadata: { connector, messageId: mail.id, attachmentId: a.id, subject: mail.subject, contentType: a.contentType },
        }));
        return of({ items, totalRecords: items.length });
      }
      return of({ items: [], totalRecords: 0 });
    }
    const pageSize = event.pageSize || 20;
    const key = `${event.tabId}:${connector}:root`;
    const saved = this.integrationPaginationState[key];
    const params: any = { connector, pageSize };
    if (event.page > 0 && saved?.nextPageToken) params.pageToken = saved.nextPageToken;
    return this.integrationsService.listMails(params).pipe(
      map((res: any) => {
        const mails: MailMessageInfo[] = res?.data?.data ?? [];
        mails.forEach((mail) => this.outlookMailsCache.set(mail.id, mail));
        const items: FileItem[] = mails
          .filter((mail) => mail.hasAttachments)
          .map((mail) => ({
            id: mail.id,
            name: mail.subject ?? 'No Subject',
            isFolder: true,
            folderIcon: 'mail',
            folderIconSource: 'lucide',
            metadata: { connector, messageId: mail.id, subject: mail.subject },
          }));
        const nextPageToken = res?.data?.nextPageToken;
        if (nextPageToken) this.integrationPaginationState[key] = { nextPageToken };
        return { items, totalRecords: items.length + (res?.data?.hasMore ? pageSize : 0) };
      }),
      catchError(() => of({ items: [], totalRecords: 0 }))
    );
  }

  private normalizeIntegrationListResponse(connector: string, data: any, pageSize: number, siteUrl?: string): { items: FileItem[]; nextPageToken?: string; nextCursor?: string; hasMore?: boolean } {
    const items: FileItem[] = [];
    let rawList: any[] = [];
    if (Array.isArray(data)) rawList = data;
    else if (data?.files) rawList = data.files;
    else if (data?.entries) rawList = data.entries;
    else if (data?.value) rawList = data.value;
    else if (data?.children) rawList = data.children;
    for (const entry of rawList) {
      const isFolder = entry.mimeType === 'application/vnd.google-apps.folder' || entry.folder != null || entry['.tag'] === 'folder' || entry.file?.isFolder || entry.type === 'folder';
      const name = entry.name ?? entry.path_display?.split('/').pop() ?? 'Unknown';
      let id: string;
      let metadata: Record<string, unknown> = { connector };
      if (connector === TOOLKIT_PROVIDERS.GOOGLE_DRIVE) { id = entry.id ?? ''; metadata = { ...metadata, fileId: entry.id, mime_type: entry.mimeType }; }
      else if (connector === TOOLKIT_PROVIDERS.DROPBOX) { id = entry.path_lower ?? entry.path_display ?? entry.id ?? ''; metadata = { ...metadata, path: entry.path_lower ?? entry.path_display }; }
      else if (connector === TOOLKIT_PROVIDERS.ONE_DRIVE) { id = this.buildOneDrivePath(entry); metadata = { ...metadata, fileId: entry.id, file_name: entry.name }; }
      else if (connector === TOOLKIT_PROVIDERS.SHAREPOINT) {
        const relativeUrl = entry.serverRelativeUrl ?? entry.id ?? entry.path ?? '';
        id = isFolder && siteUrl ? `${siteUrl}|||${relativeUrl}` : relativeUrl;
        metadata = { ...metadata, serverRelativeUrl: relativeUrl, path: entry.path ?? relativeUrl, ...(siteUrl ? { siteUrl: siteUrl.replace(/\/+$/, '') } : {}) };
      } else { id = entry.id ?? entry.path ?? ''; metadata = { ...metadata, fileId: entry.id, path: entry.path }; }
      items.push({ id, name, size: entry.size ?? entry.file?.size, isFolder: !!isFolder, metadata: { ...metadata, connector } });
    }
    return { items, nextPageToken: data?.nextPageToken ?? (data as any)?.['@odata.nextLink'], nextCursor: data?.cursor, hasMore: !!data?.nextPageToken || !!data?.cursor || data?.has_more === true };
  }

  private buildOneDrivePath(entry: any): string {
    const parentPath = entry.parentReference?.path || '/drive/root:';
    const cleanParent = parentPath.replace('/drive/root:', '');
    if (!cleanParent) return `/${entry.name}`;
    return `${cleanParent}/${entry.name}`;
  }

  private normalizeSharepointSites(data: any): { items: FileItem[]; nextPageToken?: string; nextCursor?: string; hasMore?: boolean } {
    const rawList: any[] = Array.isArray(data) ? data : [];
    return { items: rawList.map((site: any) => ({ id: `${site.WebId}|||${site.Path ?? site.SiteName ?? ''}`, name: site.Title ?? 'Unknown Site', isFolder: true, metadata: { connector: TOOLKIT_PROVIDERS.SHAREPOINT, siteUrl: site.Path ?? site.SiteName, siteId: site.SiteId, webId: site.WebId, isSite: true } })) };
  }

  onFileSelectionSubmit(event: FileSelectionSubmitEvent): void {
    const newRefs: { _id: string; name: string; docModel?: string; metadata?: Record<string, any> }[] = [];
    const docModelFromTab = (tabId: string, dropdownId?: string) => {
      if (tabId === INTEGRATIONS_TAB_ID && dropdownId) return dropdownId;
      if (tabId === QueryOptions.KNOWLEDGE_BASE || tabId === QueryOptions.PROJECT_KNOWLEDGE) return DOCUMENT_MODEL.REPOSITORY;
      if (tabId === QueryOptions.PROJECT_FILES) return DOCUMENT_MODEL.CONTRACT;
      return DOCUMENT_MODEL.ATTACHMENT;
    };
    const docModelFromFile = (file: FileItem) => {
      if (file.metadata?.connector && INTEGRATIONS_TOOLKIT_PROVIDERS.includes(file.metadata.connector as any)) return file.metadata.connector as string;
      if (file.metadata?.isProjectKnowledge || file.metadata?.kbId) return DOCUMENT_MODEL.REPOSITORY;
      if (file.metadata?.projectId) return DOCUMENT_MODEL.CONTRACT;
      return DOCUMENT_MODEL.ATTACHMENT;
    };
    if (event.selectedFiles?.length) {
      event.selectedFiles.forEach(group => {
        const docModel = docModelFromTab(group.tabId, group.dropdownId ?? undefined);
        group.files.forEach(file => {
          if (file.id === this.docId) return;
          if (!file.metadata?.isAttachment) {
            const isIntegration = group.tabId === INTEGRATIONS_TAB_ID && group.dropdownId;
            const metadata = isIntegration ? this.buildIntegrationMetadata(file, group.dropdownId!) : file.metadata ? { ...file.metadata } : undefined;
            newRefs.push({ _id: file.id, name: file.name, docModel, ...(metadata && { metadata }) });
          }
        });
      });
    } else {
      (event.allSelectedFiles ?? []).forEach(file => {
        if (file.id === this.docId) return;
        if (!file.metadata?.isAttachment) {
          const docModel = docModelFromFile(file);
          const isIntegration = INTEGRATIONS_TOOLKIT_PROVIDERS.includes(docModel as any);
          const metadata = isIntegration ? this.buildIntegrationMetadata(file, docModel) : file.metadata ? { ...file.metadata } : undefined;
          newRefs.push({ _id: file.id, name: file.name, docModel, ...(metadata && { metadata }) });
        }
      });
    }
    const uploadedDocs: File[] = event.uploadedFiles || [];
    uploadedDocs.forEach(file => {
      newRefs.push({
        docModel: DOCUMENT_MODEL.UPLOADED_DOCUMENT,
        _id: file.name,
        name: file.name,
      });
    });

    const existingIds = new Set(this.referenceDocuments.map(d => d._id));
    newRefs.forEach(ref => { if (!existingIds.has(ref._id)) { this.referenceDocuments.push(ref); existingIds.add(ref._id); } });
    this.uploadedFiles = uploadedDocs;
    this.uploadDocumentsDialogVisible = false;
    this.syncFileSelectionInitialFiles();
  }

  onFileSelectionCancel(): void {
    this.uploadDocumentsDialogVisible = false;
  }

  private buildIntegrationMetadata(file: FileItem, connector: string): Record<string, any> {
    const meta: Record<string, any> = { connector, file_name: file.name, mime_type: file.metadata?.['mime_type'] };
    if (connector === 'googledrive' || connector === 'one_drive') meta['fileId'] = file.metadata?.['fileId'] ?? file.id;
    else if (connector === 'dropbox') meta['path'] = file.metadata?.['path'] ?? file.id;
    else if (connector === 'share_point') { meta['siteUrl'] = (file.metadata?.['siteUrl'] ?? '').toString().replace(/\/+$/, ''); meta['serverRelativeUrl'] = file.metadata?.['serverRelativeUrl'] ?? file.metadata?.['path'] ?? file.id; }
    else meta['fileId'] = file.id;
    return meta;
  }

  // Team
  onShareUpdateUser(user: IShareUser): void {
    const idx = this.shareDetails.users.findIndex(u => u.email === user.email);
    if (idx !== -1) { this.shareDetails.users = this.shareDetails.users.slice(); this.shareDetails.users[idx] = user; }
  }

  onRemoveUserFromShare(user: IShareUser | Partial<IShareUser>): void {
    if (user?.email) this.shareDetails.users = this.shareDetails.users.filter(u => u.email !== user.email);
  }

  // Generation
  checkJobInProgress(): void {
    this.generationInProgress = true;
    this.contractService.checkGenWithAiInProgress(DOCUMENT_CREATION_SOURCE.STANDARD).subscribe({
      next: res => {
        if (res?.data) { this.toastService.info('A Document is already being generated, please try after some time.'); this.cancel(); }
        this.generationInProgress = false;
      },
      error: () => { this.generationInProgress = false; }
    });
  }

  callAIAssistant(): void {
    const agentRemaining = this.userCreditsState.agentRemaining();
    if (agentRemaining <= 0) {
      this.toastService.error("You've run out of credits. Request more to continue.");
      return;
    }
    if (agentRemaining < 3) {
      this.toastService.warn(`Low credits (${agentRemaining} remaining). This task may not complete.`);
    }
    this.generationInProgress = true;
    this.aiAssistant.loading = true;
    const stages = [
      'Igniting creativity â€“ Starting with AI!',
      'Setting the foundation â€“ Preparing your document.',
      'AI is on it â€“ your document is taking shape!',
      'Enhancing details â€“ Refining the context.',
      'Reviewing for consistency â€“ Preparing the final version.',
      'Finalizing details - almost there!'
    ];
    let stageIndex = 0;
    const interval = setInterval(() => {
      if (stageIndex < stages.length) { this.aiAssistant.stage = stages[stageIndex]; this.aiAssistant.loadingPercentage = Math.min((stageIndex + 1) * 15, 95); stageIndex++; }
      else clearInterval(interval);
    }, 3000);

    const template = this.selectedLibraryItemId ?? '';
    const shareUsers = this.shareDetails.users?.length
      ? this.shareDetails.users.map(u => ({ email: u.email, name: u.name, user: (u.user as any)?._id ?? (typeof u.user === 'string' ? u.user : undefined), role: Array.isArray(u.role) ? u.role : u.role ? [u.role] : [SHARE_ACCESS.VIEW] }))
      : undefined;

    this.toastService.info('The document is being generated.');
    const formData = new FormData();
    formData.append('project', this.selectedProjectId);
    formData.append('dueDate', this.dueDate ? this.dueDate.toISOString() : '');
    formData.append('prompt', this.content);
    formData.append('docTypeId', this.selectedDocType?._id ?? '');
    formData.append('tone', this.selectedTone.value as string);
    formData.append('customPrompt', this.content);
    formData.append('template', template);
    formData.append('referenceDocuments', JSON.stringify(this.referenceDocuments
      .filter(d => d._id !== this.docId)
      .map(d => ({ docModel: d.docModel ?? DOCUMENT_MODEL.CONTRACT, name: d.name, id: d._id, ...(d.metadata && { metadata: d.metadata }) }))));
    formData.append('source', DOCUMENT_CREATION_SOURCE.STANDARD);
    formData.append('parentDocId', this.docId);
    formData.append('milestones', JSON.stringify(this.milestones));
    if (shareUsers) {
      formData.append('shareUsers', JSON.stringify(shareUsers));
    }
    this.uploadedFiles.forEach(file => formData.append('uploadedFiles', file));
    this.contractService.generateContractWithAi(formData as any).subscribe(res => {
      clearInterval(interval);
      this.generationInProgress = false;
      this.aiAssistant.loading = false;
      if (res.success) {
        this.onGenerate.emit({ docTypeId: this.selectedDocType?._id ?? '', documentId: res.data });
        this.resetAll();
        this.cancel();
      } else {
        this.toastService.error('Error generating document');
      }
    });
  }
}
